/**
 * The to-do inbox of the home page (T11-10, design/04 §0 "To-do แทน dashboard ตัวเลข", §2):
 * every row = a count from the owning service + the screen that is already filtered to exactly
 * those items (no filtering again). Phase 1 only — the channel rows of design/04 §2 (eBay token,
 * Whatnot CSV, listing errors) stay hidden behind their flags (R18).
 *
 * `rowsFrom()` is pure (tested): it turns the answers of the sources into rows.
 */
import type { Permission } from "@tcg/api-client";

import { writeTableParams } from "@/components/data-table/url-state";

/** Where a row leads (route + table filters in the URL — T11-05 url-state). */
export function tableLink(
  path: string,
  filters: Record<string, string | string[]> = {},
  extra: Record<string, string> = {},
): string {
  const params = writeTableParams({ filters, sort: null, view: null, limit: 25, cursor: null });
  for (const [key, value] of Object.entries(extra)) {
    params.set(key, value);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export type TodoSource =
  | "payments"
  | "orders"
  | "catalog"
  | "conflicts"
  | "discrepancies"
  | "holds"
  | "inbox"
  | "taxfree"
  | "settlements"
  | "kobutsu"
  | "pos";

export type TodoKey =
  | "bank_transfers"
  | "payments_overdue"
  | "to_ship"
  | "pickup_ready"
  | "export_missing_evidence"
  | "returns"
  | "flagged"
  | "no_photos"
  | "no_price"
  | "unverified_compare_at"
  | "prices_to_check"
  | "sold_twice"
  | "stock_differences"
  | "old_holds"
  | "undelivered_messages"
  | "taxfree_failed"
  | "taxfree_deadline"
  | "taxfree_refund_due"
  | "payouts_due"
  | "kobutsu_incomplete"
  | "shift_variance";

export interface TodoRowDef {
  key: TodoKey;
  source: TodoSource;
  /** All of these are needed to see the row (the target screen checks the same). */
  permissions: readonly Permission[];
  to: string;
  /** red = something is wrong / a deadline; amber = waiting for the shop. */
  tone: "problem" | "pending";
}

export const TODO_ROWS: readonly TodoRowDef[] = [
  {
    key: "bank_transfers",
    source: "payments",
    permissions: ["payments.read", "payments.confirm"],
    to: "/orders/bank-transfers",
    tone: "pending",
  },
  {
    key: "payments_overdue",
    source: "payments",
    permissions: ["payments.read", "payments.confirm"],
    to: tableLink("/orders/bank-transfers", {}, { queue: "overdue" }),
    tone: "problem",
  },
  {
    key: "to_ship",
    source: "orders",
    permissions: ["orders.read", "orders.fulfill"],
    to: tableLink("/orders", {}, { view: "to_ship" }),
    tone: "pending",
  },
  {
    key: "pickup_ready",
    source: "orders",
    permissions: ["orders.read", "orders.fulfill"],
    to: "/orders/pickups",
    tone: "pending",
  },
  {
    key: "export_missing_evidence",
    source: "orders",
    // the count is order-service's view; the list with the upload is tax-service's (T11-38)
    permissions: ["orders.read", "tax.read"],
    to: "/orders/export-evidence",
    tone: "problem",
  },
  {
    key: "returns",
    source: "orders",
    permissions: ["orders.read", "orders.update"],
    to: "/orders/returns",
    tone: "pending",
  },
  {
    key: "flagged",
    source: "orders",
    permissions: ["orders.read"],
    to: tableLink("/orders", {}, { view: "flagged" }),
    tone: "problem",
  },
  {
    key: "no_photos",
    source: "catalog",
    permissions: ["products.read"],
    to: tableLink("/products", { has_media: "false" }, { view: "sys-no-photos" }),
    tone: "pending",
  },
  {
    // products without a price row yet (catalog `noPrice`) are drafts — see the T11-10 gaps
    key: "no_price",
    source: "catalog",
    permissions: ["products.read"],
    to: tableLink("/products", { status: "draft" }, { view: "sys-drafts" }),
    tone: "pending",
  },
  {
    key: "unverified_compare_at",
    source: "catalog",
    permissions: ["products.read"],
    to: tableLink("/products", { compare_at_status: "unverified" }),
    tone: "problem",
  },
  {
    // T02-30: cards for sale whose price was not checked for longer than the red number of days
    key: "prices_to_check",
    source: "catalog",
    permissions: ["products.read"],
    to: tableLink(
      "/products",
      { price_check: "alert" },
      { view: "sys-prices-to-check", sort: "price_checked_asc" },
    ),
    tone: "pending",
  },
  {
    key: "sold_twice",
    source: "conflicts",
    permissions: ["inventory.read", "inventory.conflicts.resolve"],
    to: "/inventory/conflicts",
    tone: "problem",
  },
  {
    key: "stock_differences",
    source: "discrepancies",
    permissions: ["inventory.read", "inventory.adjust"],
    to: tableLink("/inventory/conflicts", {}, { tab: "stock-differences" }),
    tone: "problem",
  },
  {
    key: "old_holds",
    source: "holds",
    permissions: ["inventory.read", "inventory.holds.release"],
    to: tableLink("/inventory/holds", { older_than_hours: "24" }),
    tone: "pending",
  },
  {
    key: "undelivered_messages",
    source: "inbox",
    permissions: ["inbox.read"],
    to: tableLink(
      "/inbox",
      {},
      { type: "dlq_message,search_dlq,notification_failed,webhook_failed" },
    ),
    tone: "problem",
  },
  {
    key: "taxfree_failed",
    source: "taxfree",
    permissions: ["tax.taxfree"],
    to: tableLink("/settings/tax/tax-free", {}, { status: "failed" }),
    tone: "problem",
  },
  {
    key: "taxfree_deadline",
    source: "inbox",
    permissions: ["inbox.read", "tax.read"],
    to: tableLink("/settings/tax/tax-free", {}, { status: "deadline_soon" }),
    tone: "problem",
  },
  {
    key: "taxfree_refund_due",
    source: "taxfree",
    permissions: ["tax.taxfree"],
    to: tableLink("/settings/tax/tax-free", {}, { status: "due" }),
    tone: "pending",
  },
  {
    key: "payouts_due",
    source: "settlements",
    permissions: ["consignment.read", "payouts.create"],
    to: "/consignment/payouts",
    tone: "pending",
  },
  {
    key: "kobutsu_incomplete",
    source: "kobutsu",
    permissions: ["kobutsu.read"],
    to: tableLink("/consignment/kobutsu-ledger", { incomplete: "true" }),
    tone: "problem",
  },
  {
    // T11-35: drawers whose 過不足 needed the manager at closing (pos-service's flag), last 7 days
    key: "shift_variance",
    source: "pos",
    permissions: ["reports.view", "pos.shift.manage"],
    to: tableLink("/pos/shifts", { difference: "true" }),
    tone: "problem",
  },
];

/** A count and whether there may be more than shown (a page of 100 was full). */
export interface Count {
  n: number;
  more?: boolean;
}

/** What each source answered (only the parts the rows need). */
export interface TodoAnswers {
  payments?: { awaiting: number; overdue: number; withNotice: number };
  orders?: { views: ReadonlyArray<{ key: string; count: number }> };
  catalog?: {
    noMedia: number;
    noPrice: number;
    unverifiedCompareAt: number;
    pricesToCheck: number;
  };
  conflicts?: Count;
  discrepancies?: Count;
  holds?: Count;
  inbox?: { byType: Readonly<Record<string, number>> };
  taxfree?: { failed: Count; due: Count };
  settlements?: Count;
  kobutsu?: { incomplete: number };
  pos?: { varianceShifts: number };
}

export const UNDELIVERED_TYPES = [
  "dlq_message",
  "search_dlq",
  "notification_failed",
  "webhook_failed",
] as const;

function countOf(answers: TodoAnswers, key: TodoKey): Count | null {
  switch (key) {
    case "bank_transfers":
      return answers.payments ? { n: answers.payments.awaiting } : null;
    case "payments_overdue":
      return answers.payments ? { n: answers.payments.overdue } : null;
    case "to_ship":
    case "pickup_ready":
    case "export_missing_evidence":
    case "returns":
    case "flagged": {
      const view = answers.orders?.views.find((v) => v.key === key);
      return answers.orders ? { n: view?.count ?? 0 } : null;
    }
    case "no_photos":
      return answers.catalog ? { n: answers.catalog.noMedia } : null;
    case "no_price":
      return answers.catalog ? { n: answers.catalog.noPrice } : null;
    case "unverified_compare_at":
      return answers.catalog ? { n: answers.catalog.unverifiedCompareAt } : null;
    case "prices_to_check":
      return answers.catalog ? { n: answers.catalog.pricesToCheck } : null;
    case "sold_twice":
      return answers.conflicts ?? null;
    case "stock_differences":
      return answers.discrepancies ?? null;
    case "old_holds":
      return answers.holds ?? null;
    case "undelivered_messages":
      return answers.inbox
        ? {
            n: UNDELIVERED_TYPES.reduce((sum, type) => sum + (answers.inbox?.byType[type] ?? 0), 0),
          }
        : null;
    case "taxfree_deadline":
      return answers.inbox ? { n: answers.inbox.byType.taxfree_deadline ?? 0 } : null;
    case "taxfree_failed":
      return answers.taxfree?.failed ?? null;
    case "taxfree_refund_due":
      return answers.taxfree?.due ?? null;
    case "payouts_due":
      return answers.settlements ?? null;
    case "kobutsu_incomplete":
      return answers.kobutsu ? { n: answers.kobutsu.incomplete } : null;
    case "shift_variance":
      return answers.pos ? { n: answers.pos.varianceShifts } : null;
  }
}

export interface TodoRow extends TodoRowDef {
  count: Count;
}

/**
 * Rows to show: allowed for this staff member, answered by their source and with something to do
 * (count > 0). Problems first, then the order of `TODO_ROWS`.
 */
export function rowsFrom(answers: TodoAnswers, can: (p: Permission) => boolean): TodoRow[] {
  const out: TodoRow[] = [];
  for (const def of TODO_ROWS) {
    if (!def.permissions.every(can)) {
      continue;
    }
    const count = countOf(answers, def.key);
    if (count && count.n > 0) {
      out.push({ ...def, count });
    }
  }
  return out.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === "problem" ? -1 : 1));
}

/** The sources this staff member may ask (a source is skipped when no row of it is allowed). */
export function sourcesFor(can: (p: Permission) => boolean): Set<TodoSource> {
  return new Set(TODO_ROWS.filter((def) => def.permissions.every(can)).map((def) => def.source));
}
